import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Text, Button, VStack, HStack, useToast } from '@chakra-ui/react';
import BackButton from '../components/BackButton';
import FavoritesButton from '../components/FavoritesButton';
import EditButton from '../components/EditButton';
import PhotoGallery from '../components/PhotoGallery';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { formatTimeRange, sendLog, sendError, clearInfluencerApplicationsCache } from '../utils';
import { ROUTES, API_ROUTES, buildRoute, APPLICATION_STATUS, EVENT_STATUS, USER_TYPES } from '../constants';


interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  location: string;
  images: string[];
  isFavorite: boolean;
  category: string;
  description: string;
  offer: string;
  rules: string;
  status?: string;
}

interface Application {
  id: string;
  eventId: string;
  influencerId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const EventDetailsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();
  const { user } = useUser();
  const toast = useToast();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [businessName, setBusinessName] = useState<string>('');

  // Check if user came from business context
  const isFromBusiness = location.state?.fromBusiness || false;

  // Fetch event data from cache or API
  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId || !config?.apiUrl) {
        setError('Event ID or API URL not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let eventData = null;

        // Try to get event from cache first
        try {
          // Check business events cache
          const businessCacheKey = `business_events_cache_${user?.id}`;
          const businessCached = sessionStorage.getItem(businessCacheKey);
          if (businessCached) {
            const businessCache = JSON.parse(businessCached);
            const cachedEvent = businessCache.data?.find((e: any) => e.id === eventId);
            if (cachedEvent) {
              eventData = cachedEvent;
              sendLog('EventDetailsLoadedFromBusinessCache', {
                eventId,
                userId: user?.id
              });
            }
          }

          // If not found in business cache, check influencer events cache
          if (!eventData) {
            const influencerCacheKey = 'influencer_events_cache';
            const influencerCached = sessionStorage.getItem(influencerCacheKey);
            if (influencerCached) {
              const influencerCache = JSON.parse(influencerCached);
              const cachedEvent = influencerCache.data?.find((e: any) => e.id === eventId);
              if (cachedEvent) {
                eventData = cachedEvent;
                sendLog('EventDetailsLoadedFromInfluencerCache', {
                  eventId,
                  userId: user?.id
                });
              }
            }
          }
        } catch (cacheError) {
          console.error('Error reading from cache:', cacheError);
          // Continue to API fetch
        }

        // If not in cache or if cached event doesn't have businessName, fetch from API
        // This ensures we always get the businessName field
        if (!eventData || !eventData.businessName) {
          const response = await axios.get(`${config.apiUrl}${buildRoute(API_ROUTES.EVENTS_BY_ID, { id: eventId })}`);
          eventData = response.data;
          sendLog('EventDetailsLoadedFromAPI', {
            eventId,
            userId: user?.id,
            fromCache: !!eventData && !eventData.businessName
          });
        }

        // Transform event data to match Event interface
        // Read timestamps from top-level fields (startTime, endTime) and convert to time strings
        const timeString = eventData.startTime !== undefined && eventData.endTime !== undefined
          ? formatTimeRange(eventData.startTime, eventData.endTime)
          : '';

        // Extract date from startTime timestamp
        let dateString = '';
        if (eventData.startTime) {
          const date = new Date(eventData.startTime);
          if (!isNaN(date.getTime())) {
            dateString = date.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            });
          }
        }

        const formattedEvent: Event = {
          id: eventData.id,
          title: eventData.eventName || '',
          date: dateString,
          time: timeString,
          location: eventData.data?.eventLocation || '',
          images: eventData.data?.photos || eventData.photos || [],
          isFavorite: false, // TODO: Fetch from favorites API
          category: eventData.category || '',
          description: eventData.data?.eventDetails || '',
          offer: eventData.data?.proposedOffer || '',
          rules: eventData.data?.eventRules || '',
          status: eventData.status
        };

        setEvent(formattedEvent);
        setError(null);

        // Set business name from event data (now included in API response)
        if (eventData.businessName) {
          setBusinessName(eventData.businessName);
          console.log('Business name set:', eventData.businessName);
        } else {
          // If businessName is still not available, clear it
          setBusinessName('');
          console.log('Business name not found in event data. businessId:', eventData.businessId);
        }

        // Record view for influencers
        if (user?.userType === USER_TYPES.INFLUENCER && eventId && config?.apiUrl) {
          try {
            await axios.post(`${config.apiUrl}${API_ROUTES.EVENT_VIEWS}`, {
              eventId
            });
            sendLog('EventViewRecorded', {
              eventId,
              userId: user.id
            });
          } catch (viewErr) {
            // Silently fail — view recording should not block page load
            console.warn('Failed to record event view:', viewErr);
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load event');
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, config?.apiUrl, user?.id]);

  // Fetch application status for influencers
  useEffect(() => {
    const fetchApplication = async () => {
      if (
        !eventId ||
        !config?.apiUrl ||
        !user?.id ||
        isFromBusiness ||
        user?.userType !== USER_TYPES.INFLUENCER
      ) {
        return;
      }

      try {
        const response = await axios.get(`${config.apiUrl}${API_ROUTES.APPLICATIONS}`, {
          params: { eventId }
        });

        // Find the application for this event (there should be only one)
        const applications = response.data;
        if (applications && applications.length > 0) {
          setApplication(applications[0]);
        } else {
          setApplication(null);
        }
      } catch (err: any) {
        // If 401 or 403, user is not authenticated or not authorized - this is ok
        if (err.response?.status === 401 || err.response?.status === 403) {
          setApplication(null);
          return;
        }
        console.error('Error fetching application:', err);
        sendError(err, {
          eventId,
          userId: user?.id,
          context: 'FetchApplicationError'
        });
      }
    };

    fetchApplication();
  }, [eventId, config?.apiUrl, user?.id, user?.userType, isFromBusiness]);

  const handleBack = () => {
    navigate(-1);
  };

  // Check if participation is allowed
  const canParticipate = () => {
    // Cannot participate if event is cancelled
    if (event?.status === EVENT_STATUS.CANCELLED) {
      return false;
    }

    // Cannot participate if application was cancelled or rejected
    if (application) {
      if (
        application.status === APPLICATION_STATUS.CANCELLED ||
        application.status === APPLICATION_STATUS.REJECTED
      ) {
        return false;
      }
    }

    return true;
  };

  const getParticipationMessage = () => {
    if (event?.status === EVENT_STATUS.CANCELLED) {
      return 'This event has been cancelled.';
    }

    if (application?.status === APPLICATION_STATUS.CANCELLED) {
      return 'You cancelled your application for this event.';
    }

    if (application?.status === APPLICATION_STATUS.REJECTED) {
      return 'Your application was declined by the business.';
    }

    return '';
  };

  const canCancelApplication = () => {
    // Cannot cancel if event is cancelled
    if (event?.status === EVENT_STATUS.CANCELLED) {
      return false;
    }

    // Can only cancel if application exists and is pending or approved
    if (
      application &&
      (application.status === APPLICATION_STATUS.PENDING || application.status === APPLICATION_STATUS.APPROVED)
    ) {
      return true;
    }

    return false;
  };

  const handleParticipate = () => {
    // Prevent participation if not allowed
    if (!canParticipate()) {
      const message = getParticipationMessage();
      toast({
        title: 'Cannot Participate',
        description: message,
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    // If application exists with pending or approved status, show cancel confirmation
    // But only if event is not cancelled
    if (canCancelApplication()) {
      setIsConfirmationDialogOpen(true);
    } else if (!event || event.status !== EVENT_STATUS.CANCELLED) {
      // Show participate confirmation (only if event is not cancelled)
      setIsConfirmationDialogOpen(true);
    } else {
      // Event is cancelled, show message
      toast({
        title: 'Event Cancelled',
        description: 'This event has been cancelled. You cannot participate or cancel your application.',
        status: 'info',
        duration: 4000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  const handleInfluencers = () => {
    if (!event) return;
    navigate(ROUTES.BUSINESS_INFLUENCERS, {
      state: {
        eventId: event.id,
        eventName: event.title,
        fromBusiness: true
      }
    });
  };

  const handleEditEvent = () => {
    if (!event) return;
    navigate(ROUTES.BUSINESS_EVENT_CREATION, {
      state: {
        editMode: true,
        eventId: event.id
      }
    });
  };

  const handlePageClick = (e: React.MouseEvent) => {
    // Close dialog if clicking anywhere outside the dialog
    if (isConfirmationDialogOpen) {
      setIsConfirmationDialogOpen(false);
    }
  };

  const handleConfirmRegistration = async () => {
    try {
      // Create application for the event
      const response = await axios.post(`${config.apiUrl}${API_ROUTES.APPLICATIONS}`, {
        eventId: eventId
      });

      // Update application state
      setApplication(response.data);

      // Close the dialog
      setIsConfirmationDialogOpen(false);

      // Show success message
      toast({
        title: 'Application Submitted',
        description: 'Your application has been submitted successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });

      // Navigate back to home
      navigate(ROUTES.INFLUENCER_HOME);
    } catch (error: any) {
      console.error('Error submitting application:', error);

      // Show error message
      const errorMessage = error.response?.data?.error || 'Failed to submit application. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  const handleCancelApplication = async () => {
    if (!application) return;

    // Prevent cancellation if event is cancelled
    if (event?.status === EVENT_STATUS.CANCELLED) {
      toast({
        title: 'Cannot Cancel',
        description: 'This event has been cancelled. You cannot cancel your application.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top'
      });
      setIsConfirmationDialogOpen(false);
      return;
    }

    try {
      // Cancel the application via DELETE endpoint
      await axios.delete(`${config.apiUrl}${buildRoute(API_ROUTES.APPLICATIONS_BY_ID, { id: application.id })}`);

      // Clear the applications cache to ensure fresh data is loaded
      clearInfluencerApplicationsCache();

      // Close the dialog
      setIsConfirmationDialogOpen(false);

      // Clear application state
      setApplication(null);

      // Show success message
      toast({
        title: 'Application Cancelled',
        description: 'Your application has been cancelled successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });

      // Navigate back to home
      navigate(ROUTES.INFLUENCER_HOME);
    } catch (error: any) {
      console.error('Error cancelling application:', error);

      // Show error message
      const errorMessage = error.response?.data?.error || 'Failed to cancel application. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top'
      });
    }
  };


  // Show loading state
  if (loading) {
    return (
      <Box className="base-container" display="flex" alignItems="center" justifyContent="center" minH="100vh">
        <Text color="#C6D959">Loading event...</Text>
      </Box>
    );
  }

  // Show error state
  if (error || !event) {
    return (
      <Box className="base-container" display="flex" alignItems="center" justifyContent="center" minH="100vh">
        <VStack spacing="20px">
          <Text color="#F00C23">{error || 'Event not found'}</Text>
          <Button onClick={handleBack}>Go Back</Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      className="base-container"
      onClick={handlePageClick}
    >
      {/* Photo Gallery */}
      <PhotoGallery
        images={event.images}
      />

      {/* Navigation Bar */}
      <Box
        position="absolute"
        top="44px"
        left="0"
        width="100%"
        height="60px"
        zIndex={1}
      >
        {/* Back Button */}
        <Box
          position="absolute"
          top="17px"
          left="24px"
        >
          <BackButton
            onClick={handleBack}
            borderColor="#C6D959"
            arrowStrokeColor="#C6D959"
            backgroundColor="rgba(198, 217, 89, 0.1)"
            borderWidth="2px"
            // size="36px"
          />
        </Box>
      </Box>

      {/* Favorite Button - Hidden for business users and when application exists */}
      {!isFromBusiness && !application && (
        <FavoritesButton
          position="absolute"
          top="240px"
          right="21px"
          isFavorite={event.isFavorite}
          onClick={(e) => {e.stopPropagation(); console.log(event.isFavorite); event.isFavorite = !event.isFavorite}}
        />
      )}

      {/* Edit Button - Only for business users */}
      {isFromBusiness && (
        <EditButton
          position="absolute"
          top="61px"
          right="21px"
          onClick={() => {
            handleEditEvent();
          }}
          zIndex={20}
        />
      )}

      {/* Business Name Header - Similar to BusinessProfilePage */}
      {businessName && (
        <Box
          position="absolute"
          top="44px"
          left="0"
          right="0"
          height="60px"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          paddingRight="24px"
        >
          <Text className="title-text" >
            {businessName}
          </Text>
        </Box>
      )}

      {/* Event Title - Fixed, non-scrollable */}
      <Text
        className="title-text"
        position="absolute"
        left="24px"
        top="300px"
        zIndex={1}
      >
        {event.title}
      </Text>

      {/* Scrollable Content Container - Starts after event title */}
      <Box
        position="absolute"
        top="342px"
        left="0"
        right="0"
        bottom="98px"
        overflowY="auto"
        overflowX="hidden"
        zIndex={1}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none'
        }}
      >
        {/* Content Sections */}
        <VStack
          spacing="20px"
          alignItems="flex-start"
          paddingLeft="24px"
          paddingRight="24px"
          paddingBottom="40px"
          width="100%"
        >
          {/* Event Info Sections */}
          <VStack
            spacing="10px"
            alignItems="flex-start"
          >
            {/* Date */}
            <HStack spacing="12px" alignItems="center">
              <Box
                className="event-details-box"
              >
                <svg width="16" height="16" viewBox="0 0 14 13" fill="none">
                  <path d="M6.98691 2.14626C5.63732 2.14626 4.28772 2.14545 2.93813 2.14626C1.90974 2.14707 1.10059 2.93684 1.14654 3.89222C1.16113 4.1944 1.33297 4.34549 1.66205 4.34549C3.21601 4.34549 4.76997 4.34712 6.32393 4.34427C6.60532 4.34386 6.82412 4.42972 6.93746 4.68199C7.07269 4.98227 6.87664 5.32406 6.53136 5.38183C6.40314 5.40299 6.27011 5.40381 6.13882 5.40381C4.57042 5.40503 3.00202 5.40869 1.43318 5.40015C1.20825 5.39892 1.14917 5.46565 1.15004 5.66788C1.15792 7.18028 1.15179 8.69269 1.1553 10.2051C1.15748 11.261 1.88523 11.9319 3.0169 11.9344C3.7534 11.936 4.49034 11.9388 5.22684 11.9478C5.47934 11.9506 5.68196 12.0475 5.76904 12.2847C5.89901 12.6391 5.67277 12.9955 5.26797 12.9963C4.29954 12.9984 3.32498 13.0203 2.36486 12.9268C1.37016 12.8299 0.694928 12.2408 0.267381 11.414C0.0752693 11.0425 0 10.6441 0 10.2307C0.00218806 8.06038 0 5.89045 0.00131284 3.7201C0.00175045 2.59221 0.899293 1.48913 2.07822 1.21286C2.45457 1.12456 2.85498 1.11032 3.24577 1.09323C3.44882 1.08428 3.52103 1.02976 3.50308 0.84381C3.49127 0.723371 3.50308 0.599677 3.51753 0.478424C3.55035 0.20703 3.79497 0.00114437 4.07461 0.000330592C4.34549 -7.62959e-05 4.59361 0.193603 4.633 0.456046C4.65619 0.609442 4.65575 0.766908 4.654 0.92234C4.65269 1.04319 4.69952 1.08876 4.83343 1.08876C6.27798 1.0855 7.72254 1.0855 9.16666 1.08876C9.30626 1.08876 9.34433 1.03261 9.33952 0.91705C9.33427 0.788473 9.33164 0.659083 9.33952 0.530506C9.35877 0.231036 9.58677 0.017013 9.89441 0.000737481C10.1723 -0.0139105 10.4265 0.191161 10.4712 0.480866C10.4917 0.614325 10.4983 0.751446 10.4913 0.886126C10.4834 1.03749 10.5469 1.08306 10.7066 1.09038C11.0694 1.10747 11.4401 1.10707 11.7915 1.18112C13.0496 1.446 13.9419 2.50229 13.9896 3.75428C14.0031 4.11316 13.997 4.47325 13.9926 4.83254C13.9887 5.15845 13.7563 5.3855 13.4321 5.39241C13.0934 5.39974 12.8724 5.20321 12.8535 4.8598C12.8286 4.40001 12.8535 3.93453 12.7875 3.48045C12.6776 2.73055 11.9144 2.14829 11.1005 2.14667C9.72899 2.14382 8.35752 2.14585 6.98604 2.14585L6.98691 2.14626Z"
                  fill="none"
                  stroke={"rgba(28, 28, 30, 0.9)"}/>
                  <path d="M9.9258 5.45333C12.0902 5.45862 13.9807 7.02351 13.9816 9.23251C13.9825 11.3337 12.2206 12.9991 9.8886 12.9962C7.60251 12.9938 5.828 11.2885 5.8455 9.1906C5.86301 7.12564 7.68391 5.44641 9.9258 5.45333ZM12.8355 9.23291C12.8302 7.71115 11.5607 6.51083 9.89998 6.51531C8.13553 6.52019 6.96448 7.88855 6.98636 9.23535C7.01086 10.7437 8.29044 11.933 9.92011 11.9277C11.527 11.9224 12.8429 10.7067 12.8355 9.23291Z"
                  fill="none"
                  stroke={"rgba(28, 28, 30, 0.9)"}/>
                  <path d="M10.5089 8.57956C10.3737 8.92379 10.6275 9.13985 10.8818 9.36648C11.0835 9.54633 11.1181 9.77378 11.0113 9.98088C10.9111 10.1754 10.6953 10.2962 10.4603 10.2767C10.3833 10.2702 10.3028 10.2466 10.2345 10.2124C10.1588 10.1746 10.0875 10.1229 10.0267 10.0659C9.25909 9.3441 9.34267 9.57969 9.33917 8.57631C9.33873 8.42047 9.33261 8.26382 9.34574 8.10879C9.37024 7.8142 9.59649 7.61524 9.89888 7.60913C10.2056 7.60262 10.4196 7.76701 10.4669 8.06241C10.4927 8.22231 10.494 8.38588 10.5085 8.57997L10.5089 8.57956Z"
                  fill="none"
                  stroke={"rgba(28, 28, 30, 0.9)"}/>
                </svg>
              </Box>
              <VStack spacing="0" alignItems="flex-start">
                <Text className="event-details-item-name" >
                  Date
                </Text>
                <Text className="event-details-item-details">
                  {event.date}
                </Text>
              </VStack>
            </HStack>

            {/* Time */}
            <HStack spacing="12px" alignItems="center">
              <Box className="event-details-box">
                <svg width="16" height="16" viewBox="0 0 13 13" fill="none">
                  <path d="M1.1211e-05 6.40797C0.0248476 2.96675 2.79553 0.0403441 6.41023 0.000429304C10.106 -0.0403001 13.002 2.82175 13 6.58311C12.998 10.0382 10.1951 13.0949 6.29053 12.998C2.82688 12.9116 -0.00650324 10.1294 1.1211e-05 6.40797ZM6.47986 11.9349C9.47732 11.9516 11.9296 9.5254 11.9353 6.52487C11.9414 3.41477 9.38408 1.07365 6.52505 1.0598C3.65136 1.04595 1.06675 3.42781 1.07245 6.45726C1.07815 9.49893 3.45837 11.9183 6.47986 11.9349Z"
                  fill={"none"}
                  stroke={"rgba(28, 28, 30, 0.9)"}/>
                  <path d="M5.97343 4.85095C5.97343 4.32228 5.9661 3.7932 5.97547 3.26454C5.98361 2.81814 6.42863 2.57092 6.79385 2.80144C6.99498 2.92811 7.04099 3.13054 7.0414 3.34885C7.04262 4.14226 7.04791 4.93526 7.03814 5.72826C7.03569 5.91561 7.08659 5.9763 7.27754 5.9706C7.7246 5.95716 8.17206 5.95919 8.61911 5.96897C9.06128 5.97874 9.3186 6.33675 9.16104 6.70617C9.0678 6.92489 8.8858 7.02671 8.66227 7.02915C7.94405 7.0373 7.22543 7.04178 6.50721 7.02793C6.16968 7.02141 5.97913 6.8027 5.97465 6.45772C5.96814 5.92213 5.97302 5.38654 5.97262 4.85095H5.97343Z"
                  fill="none"
                  stroke={"rgba(28, 28, 30, 0.9)"}/>
                </svg>
              </Box>
              <VStack spacing="0" alignItems="flex-start">
                <Text className="event-details-item-name">
                  Time
                </Text>
                <Text className="event-details-item-details" >
                  {event.time}
                </Text>
              </VStack>
            </HStack>

            {/* Dress Code */}
            <HStack spacing="12px" alignItems="center">
              <Box className="event-details-box" >
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                  <path d="M7.00699 0.0332294C7.99898 0.0332294 8.99404 -0.0166877 9.98209 0.0468034C11.0166 0.113797 11.9805 0.462779 12.7846 1.14629C13.3995 1.66867 13.8064 2.34606 13.9067 3.14254C13.9991 3.87641 14.0109 4.6256 13.9934 5.36648C13.9724 6.25141 13.1556 6.98703 12.2696 6.99141C12.0817 6.99229 11.8132 6.93449 11.7274 7.03214C11.6262 7.14686 11.6744 7.40126 11.6726 7.59524C11.6634 8.84273 11.688 10.0911 11.6433 11.3368C11.5986 12.5817 10.5497 13.7153 9.31901 13.9176C9.03302 13.9645 8.74002 13.9903 8.45009 13.9925C7.40685 14.0013 6.36318 14.0017 5.31951 13.9943C4.44708 13.9886 3.69247 13.6934 3.10034 13.0353C2.56821 12.4438 2.33609 11.7414 2.33872 10.9515C2.34222 9.73291 2.33959 8.51476 2.33959 7.29617C2.33959 6.99886 2.33872 6.98879 2.03477 7.00061C1.5022 7.02163 1.00643 6.94675 0.59255 6.56624C0.21809 6.2212 0.0214433 5.79778 0.00918028 5.29335C-0.00176885 4.83403 -0.00395868 4.37383 0.00830435 3.9145C0.050349 2.35043 0.748904 1.20541 2.18587 0.538969C2.97202 0.174223 3.79977 0.00170283 4.66431 0.000389217C5.44521 -0.000486522 6.2261 0.000389217 7.00699 0.000389217C7.00699 0.011336 7.00699 0.0222827 7.00699 0.0332294ZM3.49801 7.67799H3.49889C3.49889 8.82346 3.491 9.96893 3.50152 11.114C3.51027 12.0676 4.27058 12.8352 5.20608 12.8405C6.4026 12.8475 7.59956 12.8466 8.79608 12.8405C9.49025 12.837 10.1529 12.3873 10.3793 11.7309C10.477 11.4485 10.5173 11.1337 10.519 10.8328C10.5304 8.62992 10.523 6.42656 10.53 4.2232C10.5304 4.06338 10.5624 3.88342 10.6408 3.74811C10.7639 3.53443 11.0249 3.47357 11.2811 3.54494C11.4996 3.60581 11.6608 3.82912 11.6683 4.12249C11.6788 4.53803 11.6674 4.95401 11.6731 5.36998C11.6805 5.90812 11.5758 5.83544 12.1509 5.83412C12.6362 5.83325 12.8543 5.60906 12.8617 5.11952C12.8674 4.74733 12.8718 4.37514 12.8608 4.00339C12.8481 3.57165 12.7995 3.14385 12.5932 2.75327C12.0681 1.75931 11.1532 1.38625 10.1183 1.20322C9.958 1.17476 9.89406 1.22248 9.8415 1.38449C9.35448 2.87544 7.97008 3.6982 6.45734 3.42453C5.29674 3.21479 4.52548 2.49011 4.16547 1.35516C4.1243 1.22511 4.06956 1.18132 3.93247 1.19884C3.27684 1.28378 2.67245 1.50316 2.13988 1.89461C1.61826 2.27819 1.26439 2.79356 1.20614 3.44029C1.14964 4.06995 1.16585 4.70749 1.17986 5.34108C1.18643 5.63883 1.41899 5.82975 1.7317 5.86171C1.84732 5.87353 1.96601 5.85733 2.08119 5.87047C2.28091 5.89367 2.34923 5.81705 2.34529 5.61475C2.33521 5.09719 2.3339 4.57875 2.35142 4.06119C2.36237 3.73804 2.59055 3.53137 2.89712 3.51867C3.20458 3.50597 3.42838 3.69601 3.47568 4.02441C3.49538 4.16103 3.49757 4.3007 3.49757 4.43907C3.49933 5.51886 3.49845 6.59864 3.49845 7.67799H3.49801ZM5.36331 1.17476C5.3563 1.22336 5.34973 1.23868 5.3528 1.25138C5.3598 1.27941 5.36812 1.30787 5.38214 1.33326C5.72769 1.95504 6.248 2.31322 6.96626 2.32723C7.70292 2.34168 8.2438 1.98963 8.61476 1.36391C8.71856 1.1892 8.63097 1.15242 8.4676 1.15286C7.48963 1.15549 6.51209 1.15374 5.53411 1.15505C5.47805 1.15505 5.42243 1.16775 5.36331 1.17476Z"
                  fill="#FFFFFF"
                  stroke={"rgba(28, 28, 30, 0.9)"}/>
                </svg>
              </Box>
              <VStack spacing="0" alignItems="flex-start">
                <Text className="event-details-item-name" >
                  Dress code
                </Text>
                <Text className="event-details-item-details" >
                  Casual
                </Text>
              </VStack>
            </HStack>

            {/* Location */}
            <HStack spacing="12px" alignItems="center">
              <Box className="event-details-box" >
                <svg width="16" height="16" viewBox="0 0 10 13" fill="none">
                  <path d="M5 0C2.24 0 0 2.24 0 5C0 8.5 5 13 5 13C5 13 10 8.5 10 5C10 2.24 7.76 0 5 0ZM5 6.5C4.17 6.5 3.5 5.83 3.5 5C3.5 4.17 4.17 3.5 5 3.5C5.83 3.5 6.5 4.17 6.5 5C6.5 5.83 5.83 6.5 5 6.5Z"
                  fill={"none"}
                  stroke={"rgba(28, 28, 30, 0.9)"}/>
                </svg>
              </Box>
              <VStack spacing="0" alignItems="flex-start">
                <Text className="event-details-item-name" >
                  Location
                </Text>
                <Text className="event-details-item-details" >
                  {event.location}
                </Text>
              </VStack>
            </HStack>
          </VStack>
          {/* Event Details */}
          <Box className="event-details-long-text-box">
            <Text className="event-details-long-text-title">
              Event details
            </Text>
            <Text className="event-details-long-text">
              {event.description}
            </Text>
          </Box>

          {/* Event Rules */}
          <Box className="event-details-long-text-box">
            <Text className="event-details-long-text-title">
              Event rules
            </Text>
            <Text className="event-details-long-text">
              {event.rules}
            </Text>
          </Box>

          {/* Proposed Offer */}
          <Box className="event-details-long-text-box">
            <Text className="event-details-long-text-title">
              Proposed offer for members
            </Text>
            <Text className="event-details-long-text">
              {event.offer}
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* Modal Backdrop */}
      {isConfirmationDialogOpen && (
        <Box
          className="modal-backdrop"
          zIndex={1}
          onClick={handlePageClick}
        />
      )}

      {/* Participate/Influencers Button or Confirmation Dialog */}
      {!isConfirmationDialogOpen ? (
        <Box
          className="button-big-container"
          zIndex={2}
        >
          <Button
            className="button-big"
            onClick={isFromBusiness ? handleInfluencers : handleParticipate}
            isDisabled={
              !isFromBusiness &&
              (
                // Disable if event is cancelled (influencers can only view)
                event?.status === EVENT_STATUS.CANCELLED ||
                // Disable if cannot participate and cannot cancel
                (!canParticipate() && !canCancelApplication())
              )
            }
          >
            {isFromBusiness
              ? 'Influencers'
              : (canCancelApplication())
                ? 'Cancel'
                : 'Participate'}
          </Button>
          {!isFromBusiness && (
            event?.status === EVENT_STATUS.CANCELLED ? (
              <Text
                color="#F00C23"
                fontSize="12px"
                textAlign="center"
                marginTop="8px"
                paddingX="24px"
              >
                This event has been cancelled.
              </Text>
            ) : (!canParticipate() && !canCancelApplication() && (
              <Text
                color="#F00C23"
                fontSize="12px"
                textAlign="center"
                marginTop="8px"
                paddingX="24px"
              >
                {getParticipationMessage()}
              </Text>
            ))
          )}
        </Box>
      ) : (
        <Box
          className="bottom-sheet-dialog"
          zIndex={3}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Bottom Sheet Indicator */}
          <Box
            className="bottom-sheet-indicator"
            backgroundImage="url('/assets/images/bottom-sheet-indicator.svg')"
          />

          {/* Content */}
          <VStack
            position="relative"
            zIndex={1}
            spacing="0"
            alignItems="center"
            paddingTop="44px"
            paddingX="44px"
          >
            {/* Main Question */}
            <Text
              className="dialog-title"
              marginBottom="40px"
            >
              {canCancelApplication()
                ? 'Cancel your application?'
                : 'Confirm your registration for the event'}
            </Text>

            {/* Subtitle */}
            <Text
              className="dialog-subtitle"
              marginBottom="23px"
            >
              {canCancelApplication()
                ? 'You can\'t reapply to this event after cancelling your application.'
                : 'The business will confirm your participation upon its own discretion'}
            </Text>

            {/* Information Items */}
            {!canCancelApplication() && (
              <VStack spacing="14px" alignItems="flex-start" width="100%">
                {/* Item 1 */}
                <HStack spacing="13px" alignItems="flex-start" width="321px" height="54px">
                  <Box
                    className='dialog-text-box'
                    backgroundImage="url('/assets/images/check-icon.svg')"
                  />
                  <Text
                    className='dialog-text'
                    width="281px"
                  >
                    This event is in high demand, and the venue will carefully evaluate your profile.
                  </Text>
                </HStack>

                {/* Item 2 */}
                <HStack spacing="13px" alignItems="flex-start" width="321px" height="42px">
                  <Box
                    className='dialog-text-box'
                    backgroundImage="url('/assets/images/check-icon.svg')"
                  />
                  <Text
                    className='dialog-text'
                    width="281px"
                  >
                    If your profile was selected, your invite will appear under "Events"
                  </Text>
                </HStack>

                {/* Item 3 */}
                <HStack spacing="13px" alignItems="flex-start" width="321px" height="54px">
                  <Box
                    className='dialog-text-box'
                    backgroundImage="url('/assets/images/check-icon.svg')"
                  />
                  <Text
                    className='dialog-text'
                    width="240px"
                  >
                    Date: {event.date}
                    Start: {event.time}
                  </Text>
                </HStack>
              </VStack>
            )}

            {/* Action Button */}
            <Box
              className="button-big-dialog-container"
            >
              <Button
                className="button-big"
                onClick={canCancelApplication()
                  ? handleCancelApplication
                  : handleConfirmRegistration}
                isDisabled={event?.status === EVENT_STATUS.CANCELLED}
              >
                {canCancelApplication()
                  ? 'Confirm Cancellation'
                  : 'Confirm'}
              </Button>
            </Box>
          </VStack>
        </Box>
      )}

    </Box>
  );
};

export default EventDetailsPage;
